import { Link } from "react-router-dom";
import { ArrowUpRight, Calendar, MapPin } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useSite } from "../lib/site";
import { mediaUrl } from "../lib/api";
import { fmtGregorian, fmtHijri } from "../lib/format";
import { DemoBadge } from "./Common";

export function ProjectCard({ p, tall = false, className = "" }) {
  const { t, tr } = useI18n();
  const { label } = useSite();
  return (
    <Link to={`/developments/${p.slug}`} data-testid={`project-card-${p.slug}`} className={`on-media group relative block overflow-hidden rounded-sm border border-fg/10 ${tall ? "min-h-[520px]" : "min-h-[400px]"} ${className}`}>
      {p.cover_image ? (
        <img src={mediaUrl(p.cover_image)} alt={tr(p, "title")} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-110" />
      ) : <div className="mashrabiya absolute inset-0 bg-brand opacity-60" />}
      <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/40 to-transparent" />
      <div className="absolute start-4 top-4 flex gap-2">
        {p.is_demo && <DemoBadge testid={`project-demo-badge-${p.slug}`} />}
        <span className="rounded-sm bg-canvas/70 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-fg/90 backdrop-blur">{tr(p, "status")}</span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-6">
        <p className="eyebrow mb-3">{label("project_category", p.category)}</p>
        <h3 className="font-display text-2xl text-fg md:text-3xl">{tr(p, "title")}</h3>
        <p className="mt-2 flex items-center gap-1.5 text-sm text-fg/65"><MapPin className="h-3.5 w-3.5" />{tr(p, "location")}</p>
        <div className="mt-5 flex items-center justify-between border-t border-fg/15 pt-4 text-xs text-fg/60">
          <span>{t("common.view")}</span>
          <ArrowUpRight className="rtl-flip h-5 w-5 text-cta transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1" />
        </div>
      </div>
    </Link>
  );
}

export function ArticleCard({ a }) {
  const { t, tr, lang } = useI18n();
  const { label } = useSite();
  return (
    <Link to={`/news/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group flex flex-col overflow-hidden rounded-sm border border-fg/10 bg-surface transition-[border-color] duration-500 hover:border-cta/50">
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={mediaUrl(a.cover_image)} alt={tr(a, "title")} loading="lazy" className="h-full w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
        {a.is_demo && <DemoBadge className="absolute start-3 top-3" testid={`article-demo-badge-${a.slug}`} />}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-6">
        <p className="eyebrow">{label("article_category", a.category)}</p>
        <h3 className="font-display text-xl leading-snug text-fg group-hover:text-cta">{tr(a, "title")}</h3>
        <p className="line-clamp-2 text-sm text-fg/55">{tr(a, "excerpt")}</p>
        <p className="mt-auto flex items-center gap-2 pt-3 text-xs text-fg/45"><Calendar className="h-3.5 w-3.5" />{fmtGregorian(a.published_at, lang)} · {fmtHijri(a.published_at, lang)}</p>
        <span className="sr-only">{t("news.read")}</span>
      </div>
    </Link>
  );
}
